import { useEffect, useState, useRef } from 'react';
import useIntersection from './useIntersection';

const useImpression = (
  elementRef: React.RefObject<HTMLElement>,
  delaySeconds: number,
  action: (callbackFunction?: () => void) => void,
  options: IntersectionObserverInit = {},
  dependencies: any[] = [],
  navbarHeight: number = 0,
  bottomBarHeight: number = 0,
  isCallback: boolean = false
) => {
  const [isTriggered, setIsTriggered] = useState(false);
  const scrollStartTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout>();
  const runIntervalRef = useRef<NodeJS.Timeout>();
  const [isAtCenter, setIsAtCenter] = useState(false);

  // Track intersection with 50% visibility threshold
  const [isIntersecting] = useIntersection(elementRef, {
    threshold: 0.5,
    ...options,
  });

  // Track minimal intersection for reset logic
  const [isIntersectingSmall] = useIntersection(elementRef, {
    threshold: 0.0001,
    ...options,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!elementRef.current || !isIntersectingSmall) return;

      const element = elementRef.current;
      const elementHeightScroll = element.scrollHeight;
      const viewportHeight = window.innerHeight;

      const adjustedViewportHeight = viewportHeight - (navbarHeight + bottomBarHeight);

      const isLongerThanViewport = elementHeightScroll > adjustedViewportHeight;

      if (!isLongerThanViewport) return;

      const srollTop = window.scrollY;
      const topViewport = srollTop + navbarHeight;
      const bottomViewport = topViewport + window.innerHeight - bottomBarHeight;

      const elementTop = elementRef.current.offsetTop;
      const elementHeight = elementRef.current.offsetHeight;

      const centerOfElement = elementTop + elementHeight / 2;

      // Check if element center is on viewport
      const isCentered = bottomViewport >= centerOfElement && topViewport <= centerOfElement;

      if (isCentered && isIntersectingSmall) {
        setIsAtCenter(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Run on mount

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isIntersectingSmall]);

  useEffect(() => {
    if (isIntersectingSmall && !isTriggered) {
      if (!scrollStartTimeRef.current) {
        scrollStartTimeRef.current = Date.now();
      }
      runIntervalRef.current = setInterval(() => {
        const timeElapsed = Date.now() - scrollStartTimeRef.current;
        if (timeElapsed >= (delaySeconds || 1) * 1000 && isAtCenter) {
          action(() => {
            if (isCallback) {
              setIsTriggered(true);
            }
          });
          if (!isCallback) {
            setIsTriggered(true);
          }
          clearInterval(runIntervalRef.current);
        }
      }, 5);
    } else if (!isIntersectingSmall) {
      scrollStartTimeRef.current = 0;
      setIsAtCenter(false);
      clearInterval(runIntervalRef.current);
    }

    return () => {
      clearInterval(runIntervalRef.current);
    };
  }, [isIntersectingSmall, isAtCenter, isTriggered]);

  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;
    const elementHeightScroll = element.scrollHeight;
    const viewportHeight = window.innerHeight;

    const adjustedViewportHeight = viewportHeight - (navbarHeight + bottomBarHeight);

    const isLongerThanViewport = elementHeightScroll > adjustedViewportHeight;

    // Handle viewport intersection for normal-sized sections
    if (isIntersecting && !isTriggered && !isLongerThanViewport) {
      timerRef.current = setTimeout(
        () => {
          action(() => {
            if (isCallback) {
              setIsTriggered(true);
            }
          });
          if (!isCallback) {
            setIsTriggered(true);
          }
        },
        (delaySeconds || 1) * 1000
      );
    } else if (!isIntersecting) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }

    if (isTriggered && !isIntersectingSmall) {
      setIsTriggered(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isIntersecting, isIntersectingSmall, isTriggered, action]);

  useEffect(() => {
    // Reset triggered state when dependencies change
    setIsTriggered(false);
    scrollStartTimeRef.current = 0;
    setIsAtCenter(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (runIntervalRef.current) {
      clearInterval(runIntervalRef.current);
    }
  }, [...dependencies]);
};

export default useImpression;
